<template>
	<v-navigation-drawer class="drawer" permanent mobile-breakpoint="0" color="#17171a">
		<v-card color="#17171a" flat height="100%">
			<v-card-title class="pl-4 pt-6">
				<h1>{{ $t("PROJECT") }}</h1>
			</v-card-title>
			<v-card-text class="content-area">
				<v-row>
					<v-col cols="8">
						<v-text-field v-model="word" variant="solo" density="compact" :label="$t('list_search_title')" type="text"
							hide-details class="pt-10 pb-10 search-input" append-icon="mdi-magnify" @click:append="onSearchChange"
							@keydown.enter="getSitesEnter($event)"></v-text-field>
					</v-col>
				</v-row>
				<v-table class="pr-3 py-3 my-3 project-table">
					<template v-slot:default>
						<thead>
							<tr>
								<th class="text-left header-site-name">
									{{ $t("JOBSITE_NAME") }}
								</th>
								<th class="text-right"></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in sites" :key="item.name" @mouseenter="on_mouseenter(item)"
								@mouseleave="on_mouseleave(item)" @click="on_click(item)">
								<td>{{ item.siteName }}</td>
								<td class="text-right pa-0">
									<v-btn color="primary" height="100%" @click="gotoProject(item)">
										{{ $t("OPEN") }}
										<v-icon size="small"> icon:Arrow-Right </v-icon>
									</v-btn>
								</td>
							</tr>
						</tbody>
					</template>
				</v-table>
				<div class="d-flex justify-center">
					<v-btn v-if="!noMoreProjects" variant="flat" rounded @click="getSites(this.page + 1)">
						{{ $t("SHOW_MORE") }}
					</v-btn>
					<p v-if="noMoreProjects">{{ $t("NO_MORE_PROJECTS") }}</p>
				</div>
			</v-card-text>
		</v-card>
		<v-dialog v-model="isSelectCoordinateDialogOpen" width="800px" v-if="isSelectCoordinateDialogOpen" persistent>
			<select-site-coordinate-system :site-id="this.siteId" :coordinate-system-pf="this.siteInfoPf"
				:coordinate-system-db="this.siteInfoDb" @close="closeSelectCoordinateDialog"
				@update="updateLocalCoordinateSystem" />
		</v-dialog>
		<v-dialog v-model="isFileDialogOpen" width="600px" v-if="isFileDialogOpen" persistent>
			<local-file-upload v-bind:project="{ site_id: this.siteId }" @close="closeFileUploadDialog" />
		</v-dialog>
	</v-navigation-drawer>
</template>

<script>
import { getLoginUrl } from "@/config/v2Migration";
import { isImeComposingKeyEvent } from "@/utils/imeComposition";
import { notifyError, notifyWarning } from "@/utils/notificationUtils";
import axios from "axios";
import * as constant from "../../constant";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../utils/googleAnalytics.js";
import LocalFileUpload from "../AddData/LocalFileUpload.vue";
import SelectSiteCoordinateSystem from "../AddData/SelectSiteCoordinateSystem.vue";

export default {
	name: "ListPanel",

	components: {
		LocalFileUpload,
		SelectSiteCoordinateSystem,
	},
	data: () => ({
		sites: [],
		word: "",
		noMoreProjects: false,
		page: 1,
		limit: 10,
		projects: [
			{
				name: "test",
				siteId: "test",
			},
		],
		isFileDialogOpen: false,
		isSelectCoordinateDialogOpen: false,
		siteInfoPf: null,
		siteInfoDb: null,
		siteId: undefined,
	}),
	async mounted() {
		this.getSites();
	},
	watch: {
		sites(newValue, oldValue) {
			// pinを削除
			this.removePins(oldValue);
			// pinを追加
			this.addPins(newValue);
		},
		limit() {
			// 1ページ目に戻して検索実行
			this.getSites(1);
		},
	},
	methods: {
		on_mouseenter(item) {
			window["viewer"].updatePin(item.pinId, {
				color: "#eaaa00",
				highlight: true,
			});
		},
		on_mouseleave(item) {
			window["viewer"].updatePin(item.pinId, {
				color: "#ffffff",
				highlight: false,
			});
		},
		on_click(item) {
			window["viewer"].focusPin(item.pinId);
		},
		getSitesEnter(event) {
			// Ignore the Enter that confirms IME composition
			if (isImeComposingKeyEvent(event)) return;
			// フォーカスアウトする
			event.target.blur();
			// 検索実行
			this.onSearchChange();
		},
		async gotoProject(site) {
			this.siteId = site.siteId;

			// Google Analyticsに連携
			window["viewer"].focusPin(site.pinId);
			addClickTypeLog(GoogleAnalyticsClickTypes.SC_JOBSITE_OPEN);

			// Open Spinner
			this.$store.dispatch("updateIsLoading", true);
			try {
				this.siteInfoPf = null;
				this.siteInfoDb = null;

				try {
					// get site coordinate system from PF
					const pfLocalizationInfo = await axios.get(
						`${import.meta.env.VITE_API_BASE}/sites/${this.siteId}/coordinate-system`,
						{
							auth: this.$store.state.authObject,
						},
					);
					if (pfLocalizationInfo.status === 200) {
						this.siteInfoPf = pfLocalizationInfo.data;
					} else {
						throw new Error();
					}
				} catch (error) {
					if (error.response?.status === 404) {
						// register site coordinate system using dashboard information to platform
						try {
							await axios.post(
								`${import.meta.env.VITE_API_BASE}/sites/${this.siteId}/localizations/export`,
								{
									isDashboard: true,
								},
							);

							// get latest site coordinate system from platform
							const updatedLocalization = await axios.get(
								`${import.meta.env.VITE_API_BASE}/sites/${this.siteId}/coordinate-system`,
							);

							if (updatedLocalization.status === 200) {
								this.siteInfoPf = updatedLocalization.data;
								// update DB
								await axios.post(
									`${import.meta.env.VITE_API_BASE}/sites/epsg/${this.siteId}`,
									this.createDbSiteCoordinateSystemParam(
										updatedLocalization.data,
									),
								);
							}
						} catch (error) {
							// Ignore the error because it is treated as the site coordinate system is not registered
						}
					} else {
						throw error;
					}
				}

				// get site info from DB
				const response = await axios.get(
					`${import.meta.env.VITE_API_BASE}/sites/${this.siteId}`,
					{
						auth: this.$store.state.authObject,
					},
				);
				this.siteInfoDb = response.data;

				if (this.siteInfoPf) {
					// site coordinate system exists on PF
					if (this.isNotRegisteredSiteCoordinateSystem(this.siteInfoDb)) {
						// site coordinate system does not exist on DB
						const params = this.createDbSiteCoordinateSystemParam(
							this.siteInfoPf,
						);

						// open the site after update DB
						await this.updateLocalCoordinateSystem(this.siteId, params);
					} else if (
						this.siteInfoPf.based_on ===
						constant.coordinateSystemBasedType.LOCALIZATION
					) {
						const parameters =
							this.siteInfoPf.localization?.projection.parameters;
						// check whether coordinate system of PF and DB match
						if (
							String(
								this.truncateDecimals(parameters?.rotation_angle, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.rotation, 5) ?? null) &&
							String(
								this.truncateDecimals(parameters?.false_easting, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.origin_easting, 5) ??
									null) &&
							String(
								this.truncateDecimals(parameters?.false_northing, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.origin_northing, 5) ??
									null) &&
							String(
								this.truncateDecimals(parameters?.latitude_of_origin, 5) ??
									null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.origin_latitude, 5) ??
									null) &&
							String(
								this.truncateDecimals(parameters?.central_meridian, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.origin_longitude, 5) ??
									null) &&
							String(
								this.truncateDecimals(parameters?.scale_factor, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.scale_factor, 5) ??
									null) &&
							String(
								this.truncateDecimals(parameters?.vertical_offset, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.vertical_offset, 5) ??
									null) &&
							String(
								this.truncateDecimals(parameters?.inclination_x, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.incline_x, 5) ?? null) &&
							String(
								this.truncateDecimals(parameters?.inclination_y, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.incline_y, 5) ?? null) &&
							String(
								this.truncateDecimals(parameters?.unit?.ratio, 5) ?? null,
							) ===
								(this.truncateDecimals(this.siteInfoDb.to_meters, 5) ?? null)
						) {
							// match
							this.$router.push(`/?siteId=${this.siteId}`);
						} else {
							// unmatch
							this.isSelectCoordinateDialogOpen = true;
						}
					} else if (
						this.siteInfoPf.based_on ===
						constant.coordinateSystemBasedType.PROJECTION
					) {
						// check whether coordinate system of PF and DB match
						if (
							(this.siteInfoPf.projection.srid ?? null) ===
								(this.siteInfoDb.epsg ?? null) &&
							(this.siteInfoPf.projection.vertical_srid ?? null) ===
								(this.siteInfoDb.epsg_v ?? null)
						) {
							// match
							this.$router.push(`/?siteId=${this.siteId}`);
						} else {
							// unmatch
							this.isSelectCoordinateDialogOpen = true;
						}
					} else {
						// unexpected data
						throw new Error();
					}
				} else {
					// coordinate system does not not exist on PF or an error occurred in set up process
					if (this.isNotRegisteredSiteCoordinateSystem(this.siteInfoDb)) {
						// coordinate system does not exist on DB
						this.isFileDialogOpen = true;
					} else {
						// coordinate system exists on DB

						// register platform if using design3d data
						await this.registerSiteCoordinateSystemOnPlatform(
							this.siteId,
							false,
						);

						this.$router.push(`/?siteId=${this.siteId}`);
					}
				}
			} catch (error) {
				if (error.message.indexOf("504") > 0) {
					window.location.href = getLoginUrl();
				} else {
					notifyError(this, this.$t("MESSAGE_ERROR_GET_COORDINATE_SYSTEM"));
				}
			} finally {
				// Close Spinner
				this.$store.dispatch("updateIsLoading", false);
			}

			//remove all the pins
			this.removePins(this.sites);
		},
		/**
		 * 小数点以下の指定した桁数nを超える部分を切り捨てる
		 * @param {*} num フォーマット対象の数字
		 * @param {*} n 小数点以下の桁数
		 */
		truncateDecimals(num, n) {
			if (typeof num === "number") {
				num = num.toString();
			}

			const regex = /^[+-]?\d+(\.\d+)?$/;
			// フォーマット対象が数値形式かチェック
			if (regex.test(num)) {
				// 小数点以下の桁数がnより多い場合は余分な桁数を切り捨てる
				if (num.indexOf(".") !== -1 && num.split(".")[1].length > n) {
					return Number.parseFloat(num).toFixed(n);
				}
				// 小数点なし or 小数点以下の桁数がn以下の数値は形式だけ変換する
				return Number.parseFloat(num).toString();
			}
			// 数値でない場合はそのまま返す
			return num;
		},
		addPins(sites) {
			for (const site of sites) {
				site.pinId = window["viewer"].addPin(
					Number(site.longitude),
					Number(site.latitude),
					"#ffffff",
				);
			}
		},
		removePins(sites) {
			for (const site of sites) {
				window["viewer"].removePin(site.pinId);
			}
		},
		onSearchChange() {
			// 1ページ目に戻して検索実行
			this.getSites(1);
		},
		async getSites(pageNumber) {
			// 引数がある場合は指定されたページのデータを取得
			const page = pageNumber ? pageNumber : this.page;
			const params = {
				offset: page * this.limit - this.limit + 1,
				// 次ページがあるか判定するため+1件取得
				limit: this.limit + 1,
			};

			if (this.word) {
				params.siteName = this.word;
			}

			try {
				this.$store.dispatch("updateIsLoading", true);
				const response = await axios.get(
					`${import.meta.env.VITE_API_BASE}/sites`,
					{
						auth: this.$store.state.authObject,
						params,
					},
				);

				if (page === 1) {
					this.sites = [];
				}
				for (let i = 0; i < this.limit; i++) {
					if (i >= response.data.sites.length) {
						break;
					}
					this.sites.push(response.data.sites[i]);
				}

				// 次ページのデータがあるか判定
				this.noMoreProjects = response.data.sites.length <= this.limit;

				this.page = page;
			} catch (e) {
				console.error(e.message);
			} finally {
				this.$store.dispatch("updateIsLoading", false);
			}
		},
		//here we are sending the uploaded data to the server
		async closeFileUploadDialog(uploadedData) {
			this.isFileDialogOpen = false;
			if (uploadedData) {
				this.$router.push(`/?siteId=${this.siteId}`);
			}
		},
		closeSelectCoordinateDialog() {
			this.isSelectCoordinateDialogOpen = false;
		},
		async updateLocalCoordinateSystem(siteId, coordinateInfo) {
			this.isSelectCoordinateDialogOpen = false;
			if (coordinateInfo) {
				// Using platform site coordinate system
				try {
					// Open Spinner
					this.$store.dispatch("updateIsLoading", true);
					// Update DB
					const response = await axios.post(
						`${import.meta.env.VITE_API_BASE}/sites/epsg/${siteId}`,
						coordinateInfo,
						{
							auth: this.$store.state.authObject,
						},
					);
					if (response.status === 200) {
						this.$router.push(`/?siteId=${siteId}`);
					} else {
						throw new Error();
					}
				} catch (error) {
					const errorMessage = this.$t(
						"MESSAGE_ERROR_FAILED_SET_COORDINATE_SYSTEM",
					);
					const status = error.response?.status;
					if (status) {
						notifyError(this, this.$t("UPDATE_FAILED_EDITING_OR_UPLOADING"));
					} else {
						notifyError(this, errorMessage);
					}
				} finally {
					// Close Spinner
					this.$store.dispatch("updateIsLoading", false);
				}
			} else {
				// Using Design3D site coordinate system
				// Open Spinner
				this.$store.dispatch("updateIsLoading", true);

				// register platform if using desing3d data
				await this.registerSiteCoordinateSystemOnPlatform(siteId, false);

				// Close Spinner
				this.$store.dispatch("updateIsLoading", false);

				// Transition to project page
				this.$router.push(`/?siteId=${siteId}`);
			}
		},
		async registerSiteCoordinateSystemOnPlatform(siteId, isUsedDashboard) {
			try {
				await axios.post(
					`${import.meta.env.VITE_API_BASE}/sites/${siteId}/localizations/export`,
					{
						isDashboard: isUsedDashboard,
					},
				);
			} catch (e) {
				if (e.response.data?.existLocalizationFile === false) {
					notifyWarning(this, this.$t("MESSAGE_NOT_FOUND_LOCALIZATION_FILE"));
				} else {
					notifyError(
						this,
						this.$t("MESSAGE_ERROR_FAILED_SET_COORDINATE_SYSTEM"),
					);
				}
			}
		},
		isNotRegisteredSiteCoordinateSystem(siteInfoDb) {
			return (
				!siteInfoDb.epsg &&
				!siteInfoDb.epsg_v &&
				!siteInfoDb.rotation &&
				!siteInfoDb.origin_easting &&
				!siteInfoDb.origin_northing &&
				!siteInfoDb.origin_latitude &&
				!siteInfoDb.origin_longitude &&
				!siteInfoDb.scale_factor &&
				!siteInfoDb.vertical_offset &&
				!siteInfoDb.incline_x &&
				!siteInfoDb.incline_y &&
				!siteInfoDb.to_meters
			);
		},
		createDbSiteCoordinateSystemParam(siteInfoPf) {
			const params = {
				epsg: "",
				epsg_v: "",
				rotation: "",
				origin_easting: "",
				origin_northing: "",
				origin_latitude: "",
				origin_longitude: "",
				scale_factor: "",
				vertical_offset: "",
				incline_x: "",
				incline_y: "",
				to_meters: "",
			};
			if (
				siteInfoPf.based_on === constant.coordinateSystemBasedType.LOCALIZATION
			) {
				// site coordinate system based on "localization"
				params.rotation =
					siteInfoPf.localization?.projection.parameters?.rotation_angle;
				params.origin_easting =
					siteInfoPf.localization?.projection.parameters?.false_easting;
				params.origin_northing =
					siteInfoPf.localization?.projection.parameters?.false_northing;
				params.origin_latitude =
					siteInfoPf.localization?.projection.parameters?.latitude_of_origin;
				params.origin_longitude =
					siteInfoPf.localization?.projection.parameters?.central_meridian;
				params.scale_factor =
					siteInfoPf.localization?.projection.parameters?.scale_factor;
				params.vertical_offset =
					siteInfoPf.localization?.projection.parameters?.vertical_offset;
				params.incline_x =
					siteInfoPf.localization?.projection.parameters?.inclination_x;
				params.incline_y =
					siteInfoPf.localization?.projection.parameters?.inclination_y;
				params.to_meters =
					siteInfoPf.localization?.projection.parameters?.unit?.ratio;
			} else if (
				siteInfoPf.based_on === constant.coordinateSystemBasedType.PROJECTION
			) {
				// site coordinate system based on "projection"
				params.epsg = siteInfoPf.projection?.srid;
				params.epsg_v = siteInfoPf.projection?.vertical_srid;
			} else {
				// unexpected data
				throw new Error();
			}
			return params;
		},
	},
};
</script>

<style>
.drawer {
	width: 50vw !important;
	border-top: 1px solid #434348;
}

.content-area {
	height: calc(100% - 72px);
}

.search-input {
	width: 70%;
	height: 30px;
}

.header-site-name {
	width: 80%;
}

.v-navigation-drawer__content {
	overflow-y: hidden !important;
}

.project-table {
	max-height: calc(100% - 140px - 48px);
	overflow-y: auto;
}
</style>
